import { set } from "date-fns";
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next"; // Import useTranslation hook
import { useNavigate, useLocation } from "react-router-dom"; // Import navigation hooks
import { getAllServices } from "./servicesApi"; // Import API to fetch services
import "../styles/Responsive.css"; // Import responsive styles
import Swal from "sweetalert2";

const ServicePlanInformation = ({ service, category = "SMR + PSF", onCancel, onClose, onDraft, onPublish, planOnlyMode, editPlanMode }) => {
    const { t } = useTranslation(); // Initialize the translation hook
  const navigate = useNavigate();
  const location = useLocation();
  const showSmrPsfFields = (category || "SMR + PSF") === "SMR + PSF";

  const [planName, setPlanName] = useState("");
  const [planNameError, setPlanNameError] = useState("");
  const [psfCallingMinutes, setPsfCallingMinutes] = useState("");
  const [psfCallingMinutesError, setPsfCallingMinutesError] = useState("");
  const [psfTotalMessages, setPsfTotalMessages] = useState("");
  const [psfTotalMessagesError, setPsfTotalMessagesError] = useState("");
  const [smrCallingMinutes, setSmrCallingMinutes] = useState("");
  const [smrCallingMinutesError, setSmrCallingMinutesError] = useState("");
  const [smrTotalMessages, setSmrTotalMessages] = useState("");
  const [smrTotalMessagesError, setSmrTotalMessagesError] = useState("");
  const [totalCallingMinutes, setTotalCallingMinutes] = useState("");
  const [totalCallingMinutesError, setTotalCallingMinutesError] = useState("");
  const [totalMessages, setTotalMessages] = useState("");
  const [totalMessagesError, setTotalMessagesError] = useState("");
  const [productPrice, setProductPrice] = useState("");
  const [productPriceError, setProductPriceError] = useState("");
  const [allServices, setAllServices] = useState([]); // Store all services for duplicate check
  const servicesFetchedRef = useRef(false); // Track if services have been fetched

  // Ensure URL is /AdminHome/addPlan when ServicePlanInformation component loads
  useEffect(() => {
    if (location.pathname !== "/AdminHome/addPlan") {
      navigate("/AdminHome/addPlan", { replace: true });
    }
  }, [navigate, location.pathname]);

  // Fetch all services for duplicate plan name check - always fetch on mount and when service changes
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const services = await getAllServices("all");
        setAllServices(services);
        console.log("✅ Fetched services for duplicate check:", services.length, "services");
        console.log("Current service ID:", service?.serviceInfo?.SERVICE_ID);
        console.log("Service structure:", service);
        
        // Log the service we're looking for
        if (service?.serviceInfo?.SERVICE_ID) {
          const foundService = services.find(s => {
            const id1 = String(s.serviceInfo?.SERVICE_ID || "");
            const id2 = String(service.serviceInfo.SERVICE_ID || "");
            return id1 === id2;
          });
          if (foundService) {
            console.log("✅ Found service in fetched list:", foundService.serviceInfo?.SERVICE_NAME);
            console.log("✅ Existing plans for this service:", foundService.planInfo?.length || 0, "plans");
            console.log("✅ Plan names:", foundService.planInfo?.map(p => p.PLAN_NAME || p.planName) || []);
          } else {
            console.log("❌ Service NOT found in fetched list");
          }
        }
      } catch (error) {
        console.error("Failed to fetch services for duplicate check:", error);
      }
    };
    fetchServices();
  }, [service?.serviceInfo?.SERVICE_ID]); // Re-fetch when service changes

  // Re-validate plan name when allServices loads or service changes
  useEffect(() => {
    const inputPlanName = String(planName || "").toLowerCase().trim();
    const currentServiceName = String(
      service?.serviceInfo?.SERVICE_NAME || service?.serviceName || ""
    )
      .toLowerCase()
      .trim();
    const currentPlanId = editPlanMode
      ? String(service?.planInfo?.[0]?.PLAN_ID || service?.planInfo?.[0]?.planId || "").trim()
      : "";

    if (inputPlanName && allServices.length > 0 && currentServiceName) {
      const duplicatePlan = (allServices || []).some((s) => {
        const sName = String(s.serviceInfo?.SERVICE_NAME || "").toLowerCase().trim();
        if (!sName || sName !== currentServiceName) return false;
        const existingPlans = Array.isArray(s.planInfo)
          ? s.planInfo
          : s.planInfo
            ? [s.planInfo]
            : [];
        return existingPlans.some((p) => {
          const pName = String(p.PLAN_NAME || p.planName || "").toLowerCase().trim();
          if (!pName || pName !== inputPlanName) return false;
          const pPlanId = String(p.PLAN_ID ?? p.planId ?? "").trim();
          if (editPlanMode && currentPlanId && pPlanId === currentPlanId) return false;
          return true;
        });
      });

      if (duplicatePlan) {
        setPlanNameError("Plan name already exists for this service name. Please choose a different name.");
      } else {
        setPlanNameError("");
      }
    }
  }, [allServices, service, planName, editPlanMode]);


useEffect(() => {
  console.log("Service in Plan Info:", service);
  console.log("planOnlyMode:", planOnlyMode, "editPlanMode:", editPlanMode);

  const anyValueEntered =
    (planName && planName.trim() !== "") ||
    (productPrice && productPrice.trim() !== "") ||
    (psfCallingMinutes && psfCallingMinutes.trim() !== "") ||
    (psfTotalMessages && psfTotalMessages.trim() !== "") ||
    (smrCallingMinutes && smrCallingMinutes.trim() !== "") ||
    (smrTotalMessages && smrTotalMessages.trim() !== "") ||
    (totalCallingMinutes && totalCallingMinutes.trim() !== "") ||
    (totalMessages && totalMessages.trim() !== "");

  if (!service || !service.planInfo) {
    // No plan available, reset all fields
    if (!anyValueEntered) {
      setPlanName("");
      setPsfCallingMinutes("");
      setPsfTotalMessages("");
      setSmrCallingMinutes("");
      setSmrTotalMessages("");
      setTotalCallingMinutes("");
      setTotalMessages("");
      setProductPrice("");
    }
    return;
  }

  // Extract plan (first element if array)
  const plan = Array.isArray(service.planInfo) ? service.planInfo[0] : service.planInfo;

  if (!plan) {
    if (!anyValueEntered) {
      setPlanName("");
      setPsfCallingMinutes("");
      setPsfTotalMessages("");
      setSmrCallingMinutes("");
      setSmrTotalMessages("");
      setTotalCallingMinutes("");
      setTotalMessages("");
      setProductPrice("");
    }
    return;
  }

  // Prefill logic
  const cat = (service.serviceCategory ?? service.serviceInfo?.SERVICE_CATEGORY ?? category ?? "SMR + PSF");
  const isSmrPsf = cat === "SMR + PSF";

  if (editPlanMode || (!planOnlyMode && plan)) {
    setPlanName(plan.planName || plan.PLAN_NAME || "");
    setProductPrice(plan.planProductPrice ?? plan.PLAN_TOTAL_COST ?? "");
    if (isSmrPsf) {
      setPsfCallingMinutes(String(plan.planPsfCallMinutes ?? plan.PLAN_PSF_CALL_MINUTES ?? ""));
      setPsfTotalMessages(String(plan.planPsfMessages ?? plan.PLAN_PSF_MESSAGES ?? ""));
      setSmrCallingMinutes(String(plan.planSmrCallMinutes ?? plan.PLAN_SMR_CALL_MINUTES ?? ""));
      setSmrTotalMessages(String(plan.planSmrMessages ?? plan.PLAN_SMR_MESSAGES ?? ""));
      setTotalCallingMinutes("");
      setTotalMessages("");
    } else {
      setTotalCallingMinutes(String(plan.planTotalCallMinutes ?? plan.PLAN_TOTAL_CALL_MINUTES ?? ""));
      setTotalMessages(String(plan.planTotalMessages ?? plan.PLAN_TOTAL_MESSAGES ?? ""));
      setPsfCallingMinutes("");
      setPsfTotalMessages("");
      setSmrCallingMinutes("");
      setSmrTotalMessages("");
    }
  } else if (planOnlyMode && !editPlanMode) {
    // When adding a plan (planOnlyMode=true, editPlanMode=false), this component may re-render
    // multiple times while the parent swaps the `service` object reference.
    // Avoid wiping user-entered values during typing.
    if (!anyValueEntered) {
      setPlanName("");
      setPsfCallingMinutes("");
      setPsfTotalMessages("");
      setSmrCallingMinutes("");
      setSmrTotalMessages("");
      setTotalCallingMinutes("");
      setTotalMessages("");
      setProductPrice("");
    }
  }
}, [service, planOnlyMode, editPlanMode, category]);

  
  const validateFields = async () => {
    let isValid = true;

    const num = (v) => (v === "" || v == null ? 0 : Number(v));

    if (showSmrPsfFields) {
      if (!psfCallingMinutes || num(psfCallingMinutes) < 0) {
        setPsfCallingMinutesError("PSF Calling Minutes is required");
        isValid = false;
      } else setPsfCallingMinutesError("");
      if (!psfTotalMessages || num(psfTotalMessages) < 0) {
        setPsfTotalMessagesError("PSF Total Messages is required");
        isValid = false;
      } else setPsfTotalMessagesError("");
      if (!smrCallingMinutes || num(smrCallingMinutes) < 0) {
        setSmrCallingMinutesError("SMR Calling Minutes is required");
        isValid = false;
      } else setSmrCallingMinutesError("");
      if (!smrTotalMessages || num(smrTotalMessages) < 0) {
        setSmrTotalMessagesError("SMR Total Messages is required");
        isValid = false;
      } else setSmrTotalMessagesError("");
    } else {
      setPsfCallingMinutesError("");
      setPsfTotalMessagesError("");
      setSmrCallingMinutesError("");
      setSmrTotalMessagesError("");
      if (!totalCallingMinutes || num(totalCallingMinutes) < 0) {
        setTotalCallingMinutesError("Total Calling Minutes is required");
        isValid = false;
      } else setTotalCallingMinutesError("");
      if (!totalMessages || num(totalMessages) < 0) {
        setTotalMessagesError("Total Messages is required");
        isValid = false;
      } else setTotalMessagesError("");    }

    if (!planName.trim()) {
      setPlanNameError("Plan Name is Required");
      isValid = false;
    } else if (planName.trim().length > 15) {
      setPlanNameError("Plan name must not exceed 15 characters.");
      isValid = false;
    } else {
      const textOnlyRegex = /^[A-Za-z\s]*$/;
      if (!textOnlyRegex.test(planName)) {
        setPlanNameError(t("planNameTextOnly")); // Use translation key
        isValid = false;
      } else {
        const inputPlanName = String(planName || "").toLowerCase().trim();
        const currentServiceName = String(
          service?.serviceInfo?.SERVICE_NAME || service?.serviceName || ""
        )
          .toLowerCase()
          .trim();

        const currentPlanId = editPlanMode
          ? String(service?.planInfo?.[0]?.PLAN_ID || service?.planInfo?.[0]?.planId || "").trim()
          : "";

        // Requirement: uniqueness is based on (service name + plan name).
        if (currentServiceName) {
          let servicesToCheck = [];
          try {
            servicesToCheck = await getAllServices("all");
            setAllServices(servicesToCheck);
          } catch (error) {
            console.error("❌ validateFields - Failed to fetch services:", error);
            servicesToCheck = allServices;
          }

          const duplicatePlan = (servicesToCheck || []).some((s) => {
            const sName = String(s.serviceInfo?.SERVICE_NAME || "").toLowerCase().trim();
            if (!sName || sName !== currentServiceName) return false;

            const existingPlans = Array.isArray(s.planInfo)
              ? s.planInfo
              : s.planInfo
                ? [s.planInfo]
                : [];
            return existingPlans.some((p) => {
              const pName = String(p.PLAN_NAME || p.planName || "").toLowerCase().trim();
              if (!pName || pName !== inputPlanName) return false;

              const pPlanId = String(p.PLAN_ID ?? p.planId ?? "").trim();
              if (editPlanMode && currentPlanId && pPlanId === currentPlanId) return false;
              return true;
            });
          });

          if (duplicatePlan) {
            setPlanNameError("Plan name already exists for this service name. Please choose a different name.");
            isValid = false;
          } else {
            setPlanNameError("");
          }
        } else {
          // If service name isn't available, fall back to within-current service object only.
          if (service?.planInfo && Array.isArray(service.planInfo)) {
            const duplicatePlan = service.planInfo.find(
              (p) =>
                String(p.PLAN_NAME || p.planName || "").toLowerCase().trim() === inputPlanName &&
                (!editPlanMode || String(p.PLAN_ID ?? p.planId ?? "").trim() !== currentPlanId)
            );

            if (duplicatePlan) {
              setPlanNameError("Plan name already exists for this service. Please choose a different name.");
              isValid = false;
            } else {
              setPlanNameError("");
            }
          } else {
            setPlanNameError("");
          }
        }
      }
    }

    if (!productPrice || parseFloat(productPrice) <= 0) {
      setProductPriceError("Price is Required");
      isValid = false;
    } else {
      setProductPriceError("");
    }

    return isValid;
  };

  const handlePlanNameChange = async (event) => {
    const value = event.target.value;
    
    // Prevent entering more than 15 characters
    if (value.length > 15) {
      setPlanNameError("Plan name must not exceed 15 characters.");
      return;
    }
    
    setPlanName(value);
    
    if (value.trim() === "") {
      setPlanNameError("Plan Name is Required");
      return;
    }
    
    const textOnlyRegex = /^[A-Za-z\s]*$/;
    if (!textOnlyRegex.test(value)) {
      setPlanNameError(t("planNameTextOnly")); // Use translation key
      return;
    }
    
    // Check for duplicate plan name within the same service
    const currentServiceId = service?.serviceInfo?.SERVICE_ID;
    
    if (!currentServiceId) {
      // If no service ID yet (creating new service), check plans in service object
      if (service?.planInfo && Array.isArray(service.planInfo)) {
        const duplicatePlan = service.planInfo.find(
          (p) => 
            (p.PLAN_NAME || p.planName)?.toLowerCase().trim() === value.toLowerCase().trim() &&
            // Exclude current plan if editing
            (!editPlanMode || p.PLAN_ID !== service?.planInfo?.[0]?.PLAN_ID)
        );
        
        if (duplicatePlan) {
          setPlanNameError("Plan name already exists for this service. Please choose a different name.");
        } else {
          setPlanNameError("");
        }
      } else {
        setPlanNameError("");
      }
      return;
    }
    
    // Always fetch fresh services data to ensure we have latest plans
    let servicesToCheck = [];
    try {
      servicesToCheck = await getAllServices("all");
      setAllServices(servicesToCheck);
      console.log("✅ handlePlanNameChange - Fetched fresh services:", servicesToCheck.length);
    } catch (error) {
      console.error("❌ handlePlanNameChange - Failed to fetch services:", error);
      // Use cached services if fetch fails
      servicesToCheck = allServices;
      if (servicesToCheck.length === 0) {
        setPlanNameError("");
        return;
      }
    }
    
    // Find the service in allServices to get its existing plans
    const currentService = servicesToCheck.find(
      (s) => {
        const serviceId = String(s.serviceInfo?.SERVICE_ID || "");
        const currentId = String(currentServiceId || "");
        const match = serviceId === currentId;
        if (match) {
          console.log("✅ handlePlanNameChange - Found matching service:", s.serviceInfo?.SERVICE_NAME);
        }
        return match;
      }
    );
    
    if (currentService) {
      // Check existing plans in the service
      const existingPlans = Array.isArray(currentService.planInfo)
        ? currentService.planInfo
        : currentService.planInfo
          ? [currentService.planInfo]
          : [];
      console.log("✅ handlePlanNameChange - Existing plans count:", existingPlans.length);
      console.log("✅ handlePlanNameChange - Plan names:", existingPlans.map(p => p.PLAN_NAME || p.planName));
      console.log("✅ handlePlanNameChange - Checking plan name:", value);
      
      const duplicatePlan = existingPlans.find(
        (p) => {
          const planNameToCheck = String(p.PLAN_NAME || p.planName || "").toLowerCase().trim();
          const inputPlanName = String(value || "").toLowerCase().trim();
          const currentPlanId = editPlanMode ? String(service?.planInfo?.[0]?.PLAN_ID || "").trim() : "";
          const pPlanId = String(p.PLAN_ID || "").trim();
          // Check if duplicate name exists AND it's not the current plan being edited
          const isDuplicate = planNameToCheck === inputPlanName && 
                             planNameToCheck !== "" && 
                             (!editPlanMode || pPlanId !== currentPlanId);
          if (isDuplicate) {
            console.log("❌ handlePlanNameChange - DUPLICATE FOUND! Existing plan:", p);
          }
          return isDuplicate;
        }
      );
      
      if (duplicatePlan) {
        setPlanNameError("Plan name already exists for this service. Please choose a different name.");
      } else {
        setPlanNameError("");
      }
    } else {
      // Service not found in list
      console.log("❌ handlePlanNameChange - Service not found. Service ID:", currentServiceId);
      console.log("Available service IDs:", servicesToCheck.map(s => s.serviceInfo?.SERVICE_ID));
      setPlanNameError("");
    }
  };



  
  const handleNumericChange = (setter, setError, maxLen = 10) => (e) => {
    const onlyNumbers = e.target.value.replace(/\D/g, "").slice(0, maxLen);
    setter(onlyNumbers);
    if (onlyNumbers && Number(onlyNumbers) >= 0) setError("");
  };

  

  // Product Price
  const handleProductPriceChange = (e) => {
    const value = e.target.value;
  
    // Allow only integers (no decimal, no special characters, no negative)
    // Remove all non-digit characters
    const onlyNumbers = value.replace(/[^\d]/g, "");
  
    // Max 10 digits
    if (onlyNumbers.length <= 10) {
      setProductPrice(onlyNumbers);
      // Clear error if value is valid
      if (onlyNumbers && Number(onlyNumbers) > 0) {
        setProductPriceError("");
      }
    }
  };

const buildPlanPayload = () => {
  if (showSmrPsfFields) {
    const payload = {
      planName,
      psfCallingMinutes: psfCallingMinutes || "0",
      psfTotalMessages: psfTotalMessages || "0",
      smrCallingMinutes: smrCallingMinutes || "0",
      smrTotalMessages: smrTotalMessages || "0",
      productPrice,
      planTotalCallMinutes: String(Number(psfCallingMinutes || 0) + Number(smrCallingMinutes || 0)),
      planTotalMessages: String(Number(psfTotalMessages || 0) + Number(smrTotalMessages || 0)),
    };
    console.log("ServicePlanInformation - buildPlanPayload (SMR+PSF):", payload);
    return payload;
  }
  const payload = {
    planName,
    productPrice,
    planTotalCallMinutes: totalCallingMinutes || "0",
    planTotalMessages: totalMessages || "0",
    psfCallingMinutes: "0",
    psfTotalMessages: "0",
    smrCallingMinutes: "0",
    smrTotalMessages: "0",
  };
  console.log("ServicePlanInformation - buildPlanPayload (DTXC):", payload);
  return payload;
};



const handleDraft = async () => {
  const isValid = await validateFields();
  if (isValid) onDraft(buildPlanPayload());
};

const handlePublish = async () => {
  const isValid = await validateFields();
  if (!isValid) {
    Swal.fire({
      title: "Validation Error",
      text: "Mandatory Fields are Require",
      icon: "error",
      confirmButtonText: "OK",
    });
    return;
  }
  
  try {
    const payload = buildPlanPayload();
    if (!payload || typeof payload !== "object") {
      Swal.fire({ title: "Error", text: "Invalid plan data.", icon: "error", confirmButtonText: "OK" });
      return;
    }
    await onPublish(payload);
  } catch (err) {
    console.error("Publish error (full):", err);
    let msg =
      err?.response?.data?.creationMessage ??
      err?.response?.data?.message ??
      err?.message ??
      (typeof err === "string" ? err : err?.stack?.split("\n")?.[0]);
    if (!msg) msg = "Failed to publish service. Please try again.";
    if (err?.code === "ERR_NETWORK" || err?.message?.toLowerCase?.().includes("network")) {
      msg = `${msg} Check that the backend is running (e.g. http://localhost:8080) and CORS is allowed.`;
    }
    Swal.fire({ title: "Error", text: String(msg), icon: "error", confirmButtonText: "OK" });
  }
};

const handleCancelClick = () => {
  if (planOnlyMode) {
    // When in planOnlyMode (Add Plan flow), navigate to /AdminHome/services and close the component
    navigate("/AdminHome/services", { replace: true });
    if (onClose) {
      onClose();
    }
  } else {
    // When not in planOnlyMode, use the onCancel handler from parent
    onCancel();
  }
};


  return (
    <div className="responsive-plan-form">
      <h2 className="responsive-heading">
        {t("servicePlanInformation")} {/* Use translation key */}
      </h2>

      {/* Plan Name */}
      <div className="responsive-form-group">
        <label htmlFor="plan-name" className="responsive-label">
          {t("planName")} {/* Use translation key */}
          <span style={{ color: "#ef4444" }}> *</span>
        </label>
        <input
          type="text"
          id="plan-name"
          maxLength={15}
          value={planName}
          onChange={handlePlanNameChange}
          className={`responsive-input ${planNameError ? "error" : ""}`}
        />
        <span className="char-count">
            {planName.length}/15
          </span>
        {planNameError && (
          <p className="responsive-error">
            {planNameError}
          </p>
        )}
      </div>

       {/* SMR + PSF: 4 fields; DTXC: Total Calling Minutes and Total Messages */}
       {showSmrPsfFields ? (
        <>
          <div className="responsive-plan-input-row">
            <div className="responsive-plan-input-group">
              <label htmlFor="psf-calling-minutes" className="responsive-label">PSF Calling Minutes <span style={{ color: "#ef4444" }}> *</span></label>
              <input type="text" id="psf-calling-minutes" value={psfCallingMinutes} onChange={handleNumericChange(setPsfCallingMinutes, setPsfCallingMinutesError)} className={`responsive-plan-input ${psfCallingMinutesError ? "error" : ""}`} />
              {psfCallingMinutesError && <p className="responsive-error" style={{ marginTop: "4px", fontSize: "0.75rem" }}>{psfCallingMinutesError}</p>}
            </div>
            <div className="responsive-plan-input-group">
              <label htmlFor="psf-total-messages" className="responsive-label">PSF Total Messages <span style={{ color: "#ef4444" }}> *</span></label>
              <input type="text" id="psf-total-messages" value={psfTotalMessages} onChange={handleNumericChange(setPsfTotalMessages, setPsfTotalMessagesError)} className={`responsive-plan-input ${psfTotalMessagesError ? "error" : ""}`} />
              {psfTotalMessagesError && <p className="responsive-error" style={{ marginTop: "4px", fontSize: "0.75rem" }}>{psfTotalMessagesError}</p>}
            </div>
          </div>
          <div className="responsive-plan-input-row">
            <div className="responsive-plan-input-group">
              <label htmlFor="smr-calling-minutes" className="responsive-label">SMR Calling Minutes <span style={{ color: "#ef4444" }}> *</span></label>
              <input type="text" id="smr-calling-minutes" value={smrCallingMinutes} onChange={handleNumericChange(setSmrCallingMinutes, setSmrCallingMinutesError)} className={`responsive-plan-input ${smrCallingMinutesError ? "error" : ""}`} />
              {smrCallingMinutesError && <p className="responsive-error" style={{ marginTop: "4px", fontSize: "0.75rem" }}>{smrCallingMinutesError}</p>}
            </div>
            <div className="responsive-plan-input-group">
              <label htmlFor="smr-total-messages" className="responsive-label">SMR Total Messages <span style={{ color: "#ef4444" }}> *</span></label>
              <input type="text" id="smr-total-messages" value={smrTotalMessages} onChange={handleNumericChange(setSmrTotalMessages, setSmrTotalMessagesError)} className={`responsive-plan-input ${smrTotalMessagesError ? "error" : ""}`} />
              {smrTotalMessagesError && <p className="responsive-error" style={{ marginTop: "4px", fontSize: "0.75rem" }}>{smrTotalMessagesError}</p>}
            </div>
          </div>
        </>
      ) : (
        <div className="responsive-plan-input-row">
          <div className="responsive-plan-input-group">
            <label htmlFor="total-calling-minutes" className="responsive-label">Total Calling Minutes <span style={{ color: "#ef4444" }}> *</span></label>
            <input type="text" id="total-calling-minutes" value={totalCallingMinutes} onChange={handleNumericChange(setTotalCallingMinutes, setTotalCallingMinutesError)} className={`responsive-plan-input ${totalCallingMinutesError ? "error" : ""}`} />
            {totalCallingMinutesError && <p className="responsive-error" style={{ marginTop: "4px", fontSize: "0.75rem" }}>{totalCallingMinutesError}</p>}
          </div>
          <div className="responsive-plan-input-group">
            <label htmlFor="total-messages" className="responsive-label">Total Messages <span style={{ color: "#ef4444" }}> *</span></label>
            <input type="text" id="total-messages" value={totalMessages} onChange={handleNumericChange(setTotalMessages, setTotalMessagesError)} className={`responsive-plan-input ${totalMessagesError ? "error" : ""}`} />
            {totalMessagesError && <p className="responsive-error" style={{ marginTop: "4px", fontSize: "0.75rem" }}>{totalMessagesError}</p>}
          </div>
        </div>
      )}

      {/* Product Price */}
      <div className="responsive-plan-form-group">
        <label htmlFor="product-price" className="responsive-label">
          {t("productPrice")} {/* Use translation key */}
          <span style={{ color: "#ef4444" }}> *</span>
        </label>
        <div className="responsive-plan-price-container">
          <input
            type="text"
            id="product-price"
            value={productPrice}
            onChange={handleProductPriceChange}
            className={`responsive-plan-price-input ${productPriceError ? "error" : ""}`}
          />
        </div>
        {productPriceError && (
          <p className="responsive-error">
            {productPriceError}
          </p>
        )}
      </div>

      {/* Buttons */}
      <div className="responsive-button-container">
        <button
          type="button"
          onClick={handleCancelClick}
          className="responsive-button responsive-button-cancel"
        >
          {t("CANCEL_BUTTON")}
        </button>

        <button
          type="button"
          onClick={handlePublish}
          className="responsive-button responsive-button-primary"
        >
          {editPlanMode ? t("updatePlan") : t("publish")}
        </button>
      </div>
    </div>
  );
};

export default ServicePlanInformation;